import { Injectable } from '@angular/core';
import { SocketMiddlewareService } from './socket-middleware.service';
import { Subject, takeUntil } from 'rxjs';
import { ShipyardService } from './shipyard.service';
import { VesselEvents } from 'icreate/common/io-event/websocket-message';
import { Shipyard } from 'icreate/common/models/shipyard';
import { Vessel } from 'icreate/common/models/vessel';

@Injectable({
    providedIn: 'root',
})
export class VesselSocketService {
    private destroySubscriptions$: Subject<void> = new Subject();

    constructor(
        private socketService: SocketMiddlewareService,
        private shipyardService: ShipyardService
    ) {}

    private vesselCreated$() {
        return this.socketService.receiveMessageObserver(VesselEvents.CREATE_VESSEL);
    }

    vesselDeleted$() {
        return this.socketService.receiveMessageObserver(VesselEvents.DELETE_VESSEL);
    }

    vesselUpdated$() {
        return this.socketService.receiveMessageObserver(VesselEvents.UPDATE_VESSEL);
    }

    startSubscriptions() {
        this.destroySubscriptions();
        this.destroySubscriptions$ = new Subject();
        this.vesselCreated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (newVessel: Vessel) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                shipyardList.forEach(async shipyard => {
                    if (shipyard.id === newVessel.shipyardId) {
                        if (!shipyard.vessels.some(vessel => vessel.id === newVessel.id)) {
                            shipyard.vessels.push(newVessel);
                        }
                        shipyard.vessels.sort((a, b) =>
                            a.name.localeCompare(b.name, ['en', 'jp'], {
                                numeric: true,
                                sensitivity: 'variant',
                            })
                        );
                    }
                });
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.vesselDeleted$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async ({ vesselId, shipyardId }) => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                const shipyard = shipyardList.find(s => s.id === shipyardId);
                if (shipyard) {
                    const vesselIndex = shipyard.vessels.findIndex(v => v.id === vesselId);
                    if (vesselIndex !== -1) {
                        shipyard.vessels.splice(vesselIndex, 1);
                    }
                }
                this.shipyardService.updateShipyardList(shipyardList);
            });

        this.vesselUpdated$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async updatedVessel => {
                const shipyardList: Shipyard[] = await this.shipyardService.getList();
                const shipyard = shipyardList.find(s => s.id === updatedVessel.shipyardId);

                if (shipyard) {
                    shipyard.vessels.forEach((vessel, index) => {
                        if (vessel.id === updatedVessel.id) {
                            shipyard.vessels[index] = updatedVessel;
                        }
                    });
                    this.shipyardService.updateShipyardList(shipyardList);
                }
            });
    }

    destroySubscriptions() {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }
}
